import * as React from 'react';
import { styled } from '../theme-provider';
const StyledRail = styled.div.withConfig({ displayName: "StyledRail", componentId: "sc-1qy13lz" }) `border-radius:4px;position:relative;height:2px;background:${(props) => props.theme.colors.border.divider};${(props) => props.gradient &&
    `
		height: 4px;
		background: linear-gradient(to ${props.theme.direction === 'rtl' ? 'left' : 'right'}, ${props.gradient});
	`}`;
const StyledRailDotsWrapper = styled.div.withConfig({ displayName: "StyledRailDotsWrapper", componentId: "sc-18fb1o" }) `display:flex;position:relative;bottom:2px;justify-content:space-between;`;
const StyledRailDots = styled.div.withConfig({ displayName: "StyledRailDots", componentId: "sc-zqdgz0" }) `height:6px;width:6px;border-radius:3px;background:${({ theme, isInactive }) => isInactive
    ? theme.colors.border.divider
    : theme.colors.button.primary.default};`;
const StyledRailFill = styled.div.attrs((props) => ({
    style: {
        '--leftPercentStart': `${(props.theme.direction === 'rtl'
            ? 1 - props.xEndFraction
            : props.xStartFraction) * 100}%`,
        '--xScale': `${Math.abs(props.xEndFraction - props.xStartFraction)}`,
    },
})).withConfig({ displayName: "StyledRailFill", componentId: "sc-l5lav8" }) `border-radius:4px;position:absolute;width:100%;height:100%;background:${({ theme, isInactive }) => isInactive ? theme.colors.content.secondary : theme.colors.border.active};transform-origin:center left;transform:translateX(var(--leftPercentStart))scaleX(var(--xScale));`;
export const Rail = ({ children, railElementRef, backgroundGradient, fillStyle = 'auto', appearance, stateValues, min, max, showIntervals, step, }) => {
    const fillPercentages = React.useMemo(() => {
        const fills = [];
        if (fillStyle === 'none') {
            return fills;
        }
        const thumbAmount = stateValues.length;
        /** calculate "percentage" as fraction of 1 */
        const fr = (value, min, max) => (value - min) / (max - min);
        const rangeLowEnds = [min, ...stateValues];
        rangeLowEnds.forEach((lowEndValue, i) => {
            let isFilledToNextValue = false;
            switch (fillStyle) {
                case 'auto':
                    isFilledToNextValue =
                        (i === 0 && thumbAmount === 1) || (i === 1 && thumbAmount === 2);
                    break;
                case 'even':
                    isFilledToNextValue = (i + 1) % 2 === 0;
                    break;
                case 'odd':
                    isFilledToNextValue = (i + 1) % 2 !== 0;
                    break;
            }
            if (isFilledToNextValue) {
                fills.push({
                    start: fr(lowEndValue, min, max),
                    end: i < rangeLowEnds.length - 1 ? fr(rangeLowEnds[i + 1], min, max) : 1,
                });
            }
        });
        return fills;
    }, [fillStyle, max, min, stateValues]);
    const railPoints = React.useMemo(() => {
        const railPoints = [];
        if (!Array.isArray(step)) {
            const stepsAmount = showIntervals && step ? (max - min) / step + 1 : 0;
            for (let i = 0; i < stepsAmount; i++) {
                // If the current value is less or equal the the text step then we can mark it as active.
                const isInactive = fillPercentages[0].end * max <= i * (step || 1);
                railPoints.push(React.createElement(StyledRailDots, { isInactive: isInactive, key: i }));
            }
        }
        return railPoints;
    }, [min, max, step, showIntervals, fillPercentages]);
    const isDisabled = appearance === 'disabled';
    const isReadOnly = appearance === 'readonly';
    return (React.createElement(StyledRail, { gradient: backgroundGradient, ref: railElementRef },
        fillPercentages.map(({ start, end }) => (React.createElement(StyledRailFill, { key: `${start}-${end}`, xStartFraction: start, xEndFraction: end, isInactive: isDisabled || isReadOnly }))),
        React.createElement(StyledRailDotsWrapper, null, railPoints),
        children));
};
